import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  downloadInvoicePaymentReceipt,
  useRecordInvoicePayment,
  useReplaceInvoicePayment,
  useUploadInvoicePaymentReceipt,
} from '@/features/invoices/data/payments'
import {
  type ApInvoicePayment,
  type ApInvoicePaymentReceipt,
  type ApInvoiceDetail,
} from '@/features/invoices/view-models'
import { type ApInvoicePaymentInput } from '@/features/invoices/types'
import type { useAuth } from '@/hooks/use-auth'
import { validateAmount } from './formatting'
import { getApiErrorMessage } from '@/lib/api-error'

const defaultPaymentForm: ApInvoicePaymentInput = {
  payment_status: 'paid',
  paid_amount: '',
  paid_at: '',
  payment_method: '',
  receipt_reference: '',
  receipt_raw: '',
}

export function useInvoicePayment(
  invoiceId: string,
  invoice: ApInvoiceDetail | undefined,
  getAccessToken: ReturnType<typeof useAuth>['getAccessToken'],
) {
  const recordPayment = useRecordInvoicePayment(invoiceId)
  const replacePayment = useReplaceInvoicePayment(invoiceId)
  const uploadPaymentReceipt = useUploadInvoicePaymentReceipt(invoiceId)

  const [paymentOpen, setPaymentOpen] = useState(false)
  const [paymentForm, setPaymentForm] = useState<ApInvoicePaymentInput>(defaultPaymentForm)
  const [paymentToReplace, setPaymentToReplace] = useState<ApInvoicePayment | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [paymentReceipt, setPaymentReceipt] = useState<File | null>(null)
  const [paymentReceiptSource, setPaymentReceiptSource] = useState<
    'manual_upload' | 'clipboard_paste'
  >('manual_upload')
  const [receiptPreview, setReceiptPreview] = useState<{
    payment: ApInvoicePayment
    receipt: ApInvoicePaymentReceipt
  } | null>(null)
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState<string | null>(null)
  const [receiptPreviewError, setReceiptPreviewError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    let objectUrl: string | null = null
    setReceiptPreviewUrl(null)
    setReceiptPreviewError(null)
    if (!receiptPreview) return () => undefined
    void (async () => {
      try {
        const token = await getAccessToken()
        if (!token) throw new Error('Failed to acquire access token')
        const blob = await downloadInvoicePaymentReceipt(
          token,
          invoiceId,
          receiptPreview.payment.id,
          receiptPreview.receipt.id,
        )
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setReceiptPreviewUrl(objectUrl)
      } catch (error) {
        if (active)
          setReceiptPreviewError(getApiErrorMessage(error, 'Receipt preview failed'))
      }
    })()
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [getAccessToken, invoiceId, receiptPreview])

  // The page keeps this state mounted while loading; invoice callbacks are only exposed after loading.
  const openPaymentDialog = () => {
    const earlyPaymentAmount = invoice!.early_payment_terms?.amount_to_remit
    setPaymentToReplace(null)
    setVoidReason('')
    setPaymentReceipt(null)
    setPaymentReceiptSource('manual_upload')
    setPaymentForm({
      ...defaultPaymentForm,
      paid_amount:
        (earlyPaymentAmount != null ? Number(earlyPaymentAmount).toFixed(2) : '') ||
        invoice!.remaining_balance ||
        invoice!.still_owed_or_discount_amount ||
        invoice!.invoice_total ||
        '',
      paid_at: new Date().toISOString().slice(0, 16),
      payment_method: 'Bill.com / portal',
      idempotency_key: globalThis.crypto?.randomUUID?.() ?? `payment-${Date.now()}`,
    })
    setPaymentOpen(true)
  }

  const openPaymentCorrection = (payment: ApInvoicePayment) => {
    setPaymentToReplace(payment)
    setVoidReason('')
    setPaymentReceipt(null)
    setPaymentReceiptSource('manual_upload')
    setPaymentForm({
      payment_status: payment.payment_status === 'voided' ? 'paid' : payment.payment_status,
      paid_amount: payment.paid_amount ?? '',
      paid_at: payment.paid_at?.slice(0, 16) ?? new Date().toISOString().slice(0, 16),
      payment_method: payment.payment_method ?? '',
      receipt_reference: payment.receipt_reference ?? '',
      receipt_raw: payment.receipt_raw ?? '',
      idempotency_key: globalThis.crypto?.randomUUID?.() ?? `payment-${Date.now()}`,
    })
    setPaymentOpen(true)
  }

  const submitPayment = () => {
    const paymentError = validateAmount('Payment amount', paymentForm.paid_amount)
    if (paymentError) {
      toast.error(paymentError)
      return
    }
    if (paymentToReplace && !voidReason.trim()) {
      toast.error('A correction reason is required')
      return
    }
    const body = {
      ...paymentForm,
      paid_amount: paymentForm.paid_amount || null,
      paid_at: paymentForm.paid_at || null,
      payment_method: paymentForm.payment_method || null,
      receipt_reference: paymentForm.receipt_reference || null,
      receipt_raw: paymentForm.receipt_raw || null,
    }
    const complete = (payment: ApInvoicePayment, successMessage: string) => {
      if (!paymentReceipt) {
        toast.success(successMessage)
        setPaymentOpen(false)
        setPaymentForm(defaultPaymentForm)
        return
      }
      uploadPaymentReceipt.mutate(
        {
          paymentId: payment.id,
          file: paymentReceipt,
          source: paymentReceiptSource,
        },
        {
          onSuccess: () => {
            toast.success(successMessage)
            setPaymentOpen(false)
            setPaymentForm(defaultPaymentForm)
            setPaymentReceipt(null)
          },
          onError: (error) =>
            toast.error(
              getApiErrorMessage(error, 'Payment saved, but receipt upload failed'),
            ),
        },
      )
    }
    if (paymentToReplace) {
      replacePayment.mutate(
        {
          paymentId: paymentToReplace.id,
          body: { void_reason: voidReason.trim(), replacement: body },
        },
        {
          onSuccess: (result) => complete(result.replacement_payment, 'Payment corrected'),
          onError: (error) =>
            toast.error(getApiErrorMessage(error, 'Payment correction failed')),
        },
      )
      return
    }
    recordPayment.mutate(body, {
      onSuccess: (payment) => complete(payment, 'Payment record added'),
      onError: (error) =>
        toast.error(getApiErrorMessage(error, 'Payment record failed')),
    })
  }

  const downloadPaymentReceipt = async (
    payment: ApInvoicePayment,
    receipt: ApInvoicePaymentReceipt,
  ) => {
    const token = await getAccessToken()
    if (!token) {
      toast.error('Failed to acquire access token')
      return
    }
    try {
      const blob = await downloadInvoicePaymentReceipt(
        token,
        invoiceId,
        payment.id,
        receipt.id,
        true,
      )
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = receipt.file_name
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Receipt download failed'))
    }
  }
  return {
    paymentOpen,
    setPaymentOpen,
    paymentForm,
    setPaymentForm,
    paymentToReplace,
    voidReason,
    setVoidReason,
    paymentReceipt,
    setPaymentReceipt,
    paymentReceiptSource,
    setPaymentReceiptSource,
    receiptPreview,
    setReceiptPreview,
    receiptPreviewUrl,
    receiptPreviewError,
    recordPayment,
    replacePayment,
    uploadPaymentReceipt,
    openPaymentDialog,
    openPaymentCorrection,
    submitPayment,
    downloadPaymentReceipt,
  }
}
